import React, { useEffect, useRef, useState } from "react";
import { FaLinkedin, FaTiktok } from 'react-icons/fa';
import SwoodleLogo from "../../Assets/Icons/SwoodleUpdatedLogo.svg";
import BlueRing from "../../Assets/Icons/bluering.svg";
import Flower from "../../Assets/Icons/flower.svg";
import ZigZag from "../../Assets/Icons/zigzagline.svg";
import EnvelopeIcon from "../../Assets/Icons/envelopeicon.svg";
import "./SwoodleFooter.scss";

// Reusable footer for Swoodle public-facing flows
// - Newsletter signup posts to REACT_APP_NEWSLETTER_SUBSCRIBE_URL if provided, else opens mail client
// - External links open in new tab

const DEFAULT_LINKS = {
  home: "https://swoodle.com.au/",
  contact: "https://swoodle.com.au/#contact",
  privacy: "https://swoodle.com.au/privacy-policy/",
  terms: "https://swoodle.com.au/terms-and-conditions/",
  merchantBenefits: "https://swoodle.com.au/merchant-benefits/",
  fundraiser: "https://swoodle.com.au/fundraiser/",
  membersBenefits: "https://swoodle.com.au/members-benefits/",
  joinNow: "https://swoodle.com.au/join-now/",
  socials: {
    instagram: "https://www.instagram.com/swoodleau/",
    facebook: "https://www.facebook.com/swoodleau/",
    youtube: "https://www.youtube.com/@swoodleau",
    linkedin: "https://www.linkedin.com/company/swoodleau/",
    tiktok: "https://www.tiktok.com/@swoodleau",
  },
};

const NEWSLETTER_ENDPOINT = process.env.REACT_APP_NEWSLETTER_SUBSCRIBE_URL || "";

const SwoodleFooter = ({
  links = DEFAULT_LINKS,
  containerStyle,
  className = "",
  enableEntranceAnimation = false,
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const footerRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!enableEntranceAnimation) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        // Trigger as soon as the footer starts entering the viewport so the UI never looks empty
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          setInView(true);
        } else {
          setInView(false);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: [0, 0.1, 0.2, 0.4, 0.6, 0.8, 1],
      }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [enableEntranceAnimation]);

  useEffect(() => {
    if (!enableEntranceAnimation) {
      setInView(true);
    }
  }, [enableEntranceAnimation]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setViewportWidth(window.innerWidth || 0);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isValidEmail = (val) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(val);

  const onSubmitNewsletter = async (e) => {
    e?.preventDefault?.();
    setMessage("");
    if (!isValidEmail(email)) {
      setMessage("Please enter a valid email.");
      return;
    }
    // If an endpoint is configured, use it; otherwise open mail client as fallback
    if (NEWSLETTER_ENDPOINT) {
      try {
        setLoading(true);
        const resp = await fetch(NEWSLETTER_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.message || "Subscription failed");
        setMessage("Thanks! You are subscribed.");
        setEmail("");
      } catch (err) {
        setMessage(err?.message || "Subscription failed. Please try again later.");
      } finally {
        setLoading(false);
      }
    } else {
      window.location.href = `mailto:info@swoodle.com.au?subject=Subscribe%20to%20Newsletter&body=Please%20subscribe%20${encodeURIComponent(
        email
      )}%20to%20the%20newsletter.`;
    }
  };

  const A = ({ href, children, className = "hover:underline" }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );

  const getZigzagLeft = () => {
    if (!viewportWidth) return null;
    if (viewportWidth <= 480) return "-190px";
    if (viewportWidth <= 640) return "-150px";
    if (viewportWidth <= 900) return "-110px";
    if (viewportWidth <= 1200) return "-60px";
    if (viewportWidth <= 1400) return "-30px";
    return null;
  };

  const zigzagStyle = { "--pop-delay": "0.05s" };
  const zigzagLeft = getZigzagLeft();
  if (zigzagLeft !== null) {
    zigzagStyle.left = zigzagLeft;
  }

  return (
    <footer
      ref={footerRef}
      className={[
        "swoodle-footer w-full max-w-[1450px] mx-auto bg-[#242856] text-white mt-10 rounded-[32px] overflow-visible relative",
        enableEntranceAnimation && inView ? "swoodle-footer--animated" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={containerStyle}
    >
      {/* Decorative assets per figma - kept above content via z-index and inside bounds */}
      <img
        src={ZigZag}
        alt=""
        className="swoodle-footer__decor pointer-events-none select-none absolute z-20 swoodle-footer__zigzag"
        style={zigzagStyle}
      />



      <div className="relative w-full px-4 sm:px-6 pt-12 sm:pt-14 md:pt-16 pb-48 sm:pb-56 md:pb-64">
        <div className="max-w-[1180px] mx-auto">
          {/* Newsletter */}
          <div className="swoodle-footer__newsletter relative z-10 rounded-[20px] md:rounded-[32px] bg-[#242856] p-6 md:p-8 lg:p-12 mb-2 md:mb-12">
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,450px)_minmax(0,600px)] gap-4 md:gap-6 lg:gap-8 items-center">
              <div className="swoodle-footer__headline swoodle-footer__pop text-left -ml-4 md:-ml-8 lg:-ml-12" style={{ "--pop-delay": "0.1s" }}>
                <h3 className="text-[24px] sm:text-[28px] md:text-[40px] lg:text-[50px] leading-[1.3]" style={{ fontWeight: 300 }}>
                  Stay Updated With<br />
                  <span className="text-[#13A0E1] swoodle-footer__pop" style={{ "--pop-delay": "0.15s", fontWeight: 300 }}>Swoodle News</span>
                </h3>
              </div>
              <div className="swoodle-footer__cta swoodle-footer__pop text-left" style={{ "--pop-delay": "0.2s" }}>
                <p className="text-[#FFFFFF] text-[14px] sm:text-[15px] md:text-[17px] mb-3 md:mb-2 whitespace-normal lg:whitespace-nowrap -ml-4 md:-ml-0">
                  Sign up to receive the latest updates and offers from Swoodle.
                </p>
                <form
                  onSubmit={onSubmitNewsletter}
                  className="swoodle-footer__form flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4 -ml-4 md:-ml-0 lg:-ml-0"
                >
                  <div className="flex-1 w-full">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter Your Email"
                      className="swoodle-footer__input w-full !h-[60px] rounded-[30px] px-5 md:px-7 bg-transparent border border-[#D6B05F] text-[#D6B05F] placeholder:text-[#D6B05F] text-[15px] md:text-[18px] outline-none"
                      autoComplete="email"
                      style={{ height: '60px' }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="swoodle-footer__button w-full sm:w-auto sm:min-w-[200px] md:min-w-[240px] lg:min-w-[260px] !h-[60px] rounded-[30px] font-[600] text-[14px] md:text-[16px] disabled:opacity-70 px-8 md:px-10 whitespace-nowrap"
                    style={{ height: '60px' }}
                  >
                    <span>{loading ? "Submitting..." : "Join Our Newsletter"}</span>
                  </button>
                </form>
                {message && (
                  <div className="text-[11px] md:text-[12px] mt-2 text-[#C4C4C4]">{message}</div>
                )}
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 md:gap-6 lg:gap-8 mt-2 md:mt-10 px-2 md:px-0">
            <div className="md:pr-6 swoodle-footer__pop text-left" style={{ "--pop-delay": "0.3s" }}>
              <img src={SwoodleLogo} alt="Swoodle" className="w-[180px] sm:w-[200px] md:w-[220px] h-auto swoodle-footer__logo" />
              <p className="text-[#FFFFFF] text-[14px] sm:text-[15px] md:text-[17px] mt-3 md:mt-4 max-w-[500px]">Grow your business, reach new customers. Fundraise with ease, and unlock exclusive deals and savings.</p>
              <div className="flex items-center justify-start gap-4 md:gap-6 mt-3 md:mt-4 swoodle-footer__socials">
                <A href={(links.socials && links.socials.instagram) || DEFAULT_LINKS.socials.instagram}><span className="sr-only">Instagram</span><svg width="24" height="24" viewBox="0 0 24 24" fill="#FFC943" className="md:w-[26px] md:h-[26px]"><path d="M7 2C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 5 5h10c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5H7zm10 2c1.66 0 3 1.34 3 3v10c0 1.66-1.34 3-3 3H7c-1.66 0-3-1.34-3-3V7c0-1.66 1.34-3 3-3h10zm-5 3a5 5 0 1 0 .001 10.001A5 5 0 0 0 12 7zm0 2a3 3 0 1 1-.001 6.001A3 3 0 0 1 12 9zm5.25-2.75a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5z" /></svg></A>
                <A href={(links.socials && links.socials.facebook) || DEFAULT_LINKS.socials.facebook}><span className="sr-only">Facebook</span><svg width="24" height="24" viewBox="0 0 24 24" fill="#FFC943" className="md:w-[26px] md:h-[26px]"><path d="M22 12a10 10 0 1 0-11.5 9.95v-7.04H8.08V12h2.42V9.8c0-2.4 1.43-3.73 3.63-3.73 1.05 0 2.15.19 2.15.19v2.37h-1.21c-1.2 0-1.58.74-1.58 1.5V12h2.69l-.43 2.91h-2.26v7.04A10 10 0 0 0 22 12z" /></svg></A>
                <A href={(links.socials && links.socials.youtube) || DEFAULT_LINKS.socials.youtube}><span className="sr-only">YouTube</span><svg width="24" height="24" viewBox="0 0 24 24" fill="#FFC943" className="md:w-[26px] md:h-[26px]"><path d="M23.5 6.2s-.23-1.65-.95-2.37c-.9-.95-1.9-.95-2.36-1C16.83 2.5 12 2.5 12 2.5h-.01s-4.82 0-8.18.33c-.46.05-1.46.05-2.36 1-.72.72-.95 2.37-.95 2.37S.5 8.02.5 9.83v1.73c0 1.82.23 3.63.23 3.63s.23 1.65.95 2.37c.9.95 2.08.92 2.61 1.03 1.9.18 8.71.33 8.71.33s4.83 0 8.19-.33c.46-.05 1.46-.05 2.36-1 .72-.72.95-2.37.95-2.37s.23-1.82.23-3.63V9.83c0-1.82-.23-3.63-.23-3.63zM9.75 13.5V7.5l6 3-6 3z" /></svg></A>
                <A href={(links.socials && links.socials.linkedin) || DEFAULT_LINKS.socials.linkedin}><span className="sr-only">LinkedIn</span><FaLinkedin size={24} color="#FFC943" className="md:w-[26px] md:h-[26px]" /></A>
                <A href={(links.socials && links.socials.tiktok) || DEFAULT_LINKS.socials.tiktok}><span className="sr-only">TikTok</span><FaTiktok size={24} color="#FFC943" className="md:w-[26px] md:h-[26px]" /></A>
              </div>
            </div>
            <div className="md:pl-20 swoodle-footer__pop text-left" style={{ "--pop-delay": "0.4s" }}>
              <h4 className="swoodle-footer__section-title mb-3 text-[16px] md:text-[18px]">QUICK LINKS</h4>
              <ul className="space-y-2 md:space-y-3 text-[#C4C4C4] text-[14px] md:text-[16px]">
                <li><A href={links.home} className="hover:text-[#FFC943] hover:no-underline transition-colors duration-300">Home</A></li>


                <li><A href={links.contact} className="hover:text-[#FFC943] hover:no-underline transition-colors duration-300">Contact us</A></li>
                <li><A href={links.privacy} className="hover:text-[#FFC943] hover:no-underline transition-colors duration-300">Privacy Policy</A></li>
                <li><A href={links.terms} className="hover:text-[#FFC943] hover:no-underline transition-colors duration-300">T&C's</A></li>
              </ul>
            </div>
            <div className="swoodle-footer__pop text-left" style={{ "--pop-delay": "0.5s" }}>
              <h4 className="swoodle-footer__section-title mb-3 text-[16px] md:text-[18px]">RESOURCES</h4>
              <ul className="space-y-2 md:space-y-3 text-[#C4C4C4] text-[14px] md:text-[16px]">
                <li><A href={links.membersBenefits} className="hover:text-[#FFC943] transition-colors duration-300">Memberships</A></li>
                <li><A href={links.merchantBenefits} className="hover:text-[#FFC943] transition-colors duration-300">Merchant Benefits</A></li>
                <li><A href={links.fundraiser} className="hover:text-[#FFC943] transition-colors duration-300">Fundraisers</A></li>
                <li><A href={links.joinNow || DEFAULT_LINKS.joinNow} className="hover:text-[#FFC943] transition-colors duration-300">Join Now</A></li>
              </ul>
            </div>
            <div className="swoodle-footer__pop text-left md:-ml-12" style={{ "--pop-delay": "0.6s" }}>
              <h4 className="swoodle-footer__section-title mb-3 text-[16px] md:text-[18px]">GET IN TOUCH</h4>
              <ul className="space-y-2 md:space-y-3 text-[#FFFFFF] text-[14px] md:text-[16px]">
                <li className="hover:text-[#FFC943] transition-colors duration-300 cursor-default">
                  <svg className="inline mr-2 align-middle" width="20" height="20" viewBox="0 0 24 24" fill="#FFC943"><path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" /></svg>
                  105a Glenferrie Rd Malvern,<br />Victoria, 3144, Australia
                </li>
                <li>
                  <svg className="inline mr-2 align-middle" width="20" height="20" viewBox="0 0 24 24" fill="#FFC943"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21 11.72 11.72 0 003.67 1.17 1 1 0 01.88 1v3.09a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.09a1 1 0 011 .88 11.72 11.72 0 001.17 3.67 1 1 0 01-.21 1.11l-2.43 2.13z" /></svg>
                  <a href="tel:+61401811115" className="hover:text-[#FFC943] transition-colors duration-300">0401 811 115</a>
                </li>
                <li>
                  <img src={EnvelopeIcon} alt="" className="inline mr-2 align-middle" width="20" height="20" />
                  <a href="mailto:info@swoodle.com.au" className="hover:text-[#FFC943] transition-colors duration-300">info@swoodle.com.au</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Divider and copyright - aligned with content */}
      <div className="relative w-full px-4 sm:px-6 pb-10 sm:pb-12 md:pb-16 z-10">
        <div className="max-w-[1180px] mx-auto">
          {/* Divider line with decorative elements */}
          <div className="relative h-[1px] bg-[#D3D4DD] mb-6">
            {/* Three blue rings on divider */}
            <img
              src={BlueRing}
              alt=""
              className="swoodle-footer__decor pointer-events-none select-none absolute right-[140px] bottom-full mb-[54px] w-[32px] z-20"
              style={{ "--pop-delay": "0.7s" }}
            />
            <img
              src={BlueRing}
              alt=""
              className="swoodle-footer__decor pointer-events-none select-none absolute right-[102px] bottom-full mb-[54px] w-[32px] z-20"
              style={{ "--pop-delay": "0.8s" }}
            />
            <img
              src={BlueRing}
              alt=""
              className="swoodle-footer__decor pointer-events-none select-none absolute right-[64px] bottom-full mb-[54px] w-[32px] z-20"
              style={{ "--pop-delay": "0.9s" }}
            />
            {/* Flower on divider */}
            <img
              src={Flower}
              alt=""
              className="swoodle-footer__decor pointer-events-none select-none absolute right-[-12px] bottom-full mb-[0px] w-[60px] z-20"
              style={{ "--pop-delay": "1s" }}
            />
          </div>
          {/* Copyright */}
          <div className="text-left text-[#AFB1C1] text-[14px] sm:text-[16px] md:text-[18px] font-medium">
            © {new Date().getFullYear()} Swoodle. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SwoodleFooter;


